import { Download, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import type { Acta, Asamblea, ResultadoMateriaEntity } from '@condo/contracts';
import { useDescargarActa, useResultados } from './api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatLongDate, formatShortDate } from '@/lib/format/date';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const RESULTADO_CONFIG: Record<string, { label: string; variant: 'success' | 'danger' | 'warning' }> = {
  aprobada: { label: 'Aprobada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'danger' },
  sin_quorum: { label: 'Sin quorum', variant: 'warning' },
};

const ROL_LABELS: Record<string, string> = {
  administrador: 'Administrador',
  presidente: 'Presidente de mesa',
};

// ─────────────────────────────────────────────────────────────────────────────
// Tally table
// ─────────────────────────────────────────────────────────────────────────────

function TallyTable({
  asamblea,
  resultados,
}: {
  asamblea: Asamblea;
  resultados: ResultadoMateriaEntity[];
}) {
  const byMateriaId = new Map(resultados.map((r) => [r.materiaId, r]));
  const votables = asamblea.materias.filter((m) => m.categoria !== 'informativa');

  if (votables.length === 0) {
    return (
      <p className="text-sm text-[var(--color-ink-muted)]">
        Esta asamblea no tuvo materias sujetas a votacion.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>#</TableHead>
          <TableHead>Materia</TableHead>
          <TableHead>Resultado</TableHead>
          <TableHead className="text-right">Alicuota part.</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {votables.map((m, i) => {
          const r = byMateriaId.get(m.id);
          const config = r ? RESULTADO_CONFIG[r.resultado] : null;
          return (
            <TableRow key={m.id}>
              <TableCell className="text-[var(--color-ink-muted)]">{i + 1}</TableCell>
              <TableCell>
                <span className="font-semibold">{m.titulo}</span>
              </TableCell>
              <TableCell>
                {config ? (
                  <Badge variant={config.variant}>{config.label}</Badge>
                ) : (
                  <Badge variant="neutral">Pendiente</Badge>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {r ? `${(r.alicuotaParticipante * 100).toFixed(1)}%` : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  condoId: string;
  asamblea: Asamblea;
  acta: Acta;
}

export function ActaViewer({ condoId, asamblea, acta }: Props) {
  const asambleaId = asamblea.id;
  const descargarMutation = useDescargarActa(condoId, asambleaId);
  const { data: resultadosData, isLoading: loadingResultados } = useResultados(
    condoId,
    asambleaId,
  );

  const resultados = resultadosData?.items ?? [];

  async function handleDescargar() {
    try {
      const { signedUrl } = await descargarMutation.mutateAsync();
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('No se pudo obtener el enlace de descarga del acta');
    }
  }

  return (
    <article className="relative mx-auto max-w-3xl overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-10 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.10)]">
      {/* Letterhead */}
      <header className="border-b-2 border-[var(--color-ink)] pb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Acta de asamblea</p>
            <h1 className="font-display-tight mt-2 text-3xl font-semibold capitalize">
              Asamblea {asamblea.tipo}
            </h1>
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              Ley 21.442 de Copropiedad Inmobiliaria
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <Badge variant={acta.estado === 'firmada' ? 'success' : 'neutral'}>
              {acta.estado === 'firmada' ? 'Firmada' : 'Borrador'}
            </Badge>
            {acta.pdfS3Key && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void handleDescargar()}
                disabled={descargarMutation.isPending}
              >
                <Download className="h-4 w-4" />
                Descargar PDF
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Intro */}
      <section className="mt-6 text-sm text-[var(--color-ink-soft)]">
        <p>
          Con fecha{' '}
          <strong>{formatLongDate(asamblea.fechaProgramada)}</strong>, se reunio la asamblea{' '}
          <strong className="capitalize">{asamblea.tipo}</strong> de copropietarios, convocada
          segun lo dispuesto por la Ley 21.442 de Copropiedad Inmobiliaria.{' '}
          <strong>{asamblea.lugar}</strong>.
        </p>
      </section>

      {/* Texto libre — rendered as preformatted plain text (no HTML, XSS-safe) */}
      {acta.textoLibre && (
        <section className="mt-8">
          <p className="eyebrow">Desarrollo de la sesion</p>
          <pre className="mt-2 whitespace-pre-wrap rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-4 font-sans text-sm text-[var(--color-ink)]">
            {acta.textoLibre}
          </pre>
        </section>
      )}

      {/* Adjuntos */}
      {acta.adjuntos.length > 0 && (
        <section className="mt-8">
          <p className="eyebrow">Adjuntos</p>
          {/* TODO: add download links once GET /asambleas/{id}/acta/adjuntos/{adjuntoId}/descargar endpoint is implemented */}
          <ul className="mt-2 divide-y divide-[var(--color-hairline)]">
            {acta.adjuntos.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2">
                <Paperclip className="h-3.5 w-3.5 shrink-0 text-[var(--color-ink-muted)]" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[var(--color-ink)]">
                    {a.nombre}
                  </p>
                  <p className="text-xs text-[var(--color-ink-muted)]">
                    {a.contentType} · {formatShortDate(a.subidoEn)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Resultados tally */}
      <section className="mt-8">
        <p className="eyebrow mb-3">Resultados de votacion</p>
        {loadingResultados ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <TallyTable asamblea={asamblea} resultados={resultados} />
        )}
      </section>

      {/* Firmas */}
      <footer className="mt-12 border-t border-[var(--color-hairline)] pt-6">
        <p className="eyebrow mb-4">Firmas electronicas</p>
        {acta.firmas.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">Sin firmas registradas.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {acta.firmas.map((f) => (
              <div
                key={f.rol}
                className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] px-4 py-3"
              >
                <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
                  {ROL_LABELS[f.rol] ?? f.rol}
                </p>
                <p className="mt-1 break-all font-mono text-xs text-[var(--color-ink-muted)]">
                  {f.feaReferenceMock.slice(0, 40)}…
                </p>
                <p className="mt-1 text-xs text-[var(--color-ink-faint)]">
                  {formatShortDate(f.firmadoEn)}
                </p>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-[0.6875rem] text-[var(--color-ink-faint)]">
          FEA simulada — pendiente de integracion real con proveedor certificado.
        </p>
      </footer>
    </article>
  );
}
